/**
 * MigrateBrandingConsole — move ONE legacy-branding customer off scio1 (old
 * custom-domain K8s cluster) onto the scio3 branding portal.
 *
 * A bespoke card rather than an `ACTIONS` entry in AccountsConsole: the generic
 * AccountActionCard is single-step (fields → button → message) and this flow is
 * verify → review → confirm.
 *
 * What the migration writes (see 02-plan.md):
 *   • `companies.feature.brandingCustomDomain = true` — vanity hosts only,
 *     grandfathering the account onto the custom-domain add-on at NO CHARGE.
 *   • `domains.version = 2` + `cfProvisionState = 'pending'` — hands the record
 *     from the legacy nginx job to the Cloudflare job.
 *
 * ONE-WAY: no revert here. Rollback is the ops runbook (flip version back to 1 and
 * remove the override DNS record).
 */
import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Input,
  Label,
} from '@staff-app/ui';
import { cn } from '@/lib/utils';
import {
  useBrandingMigrationPreflight,
  useMigrateBrandingToScio3,
  type BrandingMigrationPreflight,
} from '../hooks/useMigrateBranding';

const STRIPE_HELP = 'Enter the Stripe Customer ID (e.g., cus_ABC123)';

const DOMAIN_TYPE_LABEL: Record<string, string> = {
  vanity: 'Custom domain (needs the add-on)',
  subhost: 'OptiSigns subdomain (plan covers it)',
  none: 'No branding record',
};

/** Dash for absent values so the operator sees "unset", not a blank cell. */
function val(v: string | number | null | undefined): string {
  return v === null || v === undefined || v === '' ? '—' : String(v);
}

export function MigrateBrandingConsole() {
  const [stripeId, setStripeId] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [pre, setPre] = useState<BrandingMigrationPreflight | null>(null);

  const [runPreflight, { loading: verifying }] = useBrandingMigrationPreflight();
  const [migrate, { loading: migrating }] = useMigrateBrandingToScio3();

  const idValid = stripeId.trim().startsWith('cus_');

  // A migrated account fails exactly one blocking check — `notMigrated` — which is
  // the SUCCESS state, not a failure. Distinguish it so the progress re-check reads
  // as "already migrated" rather than a red "Blocked".
  const blockingFailures = (pre?.checks ?? []).filter((c) => c.blocking && !c.ok);
  const alreadyMigrated =
    blockingFailures.length === 1 && blockingFailures[0].key === 'notMigrated';
  const provisioned = pre?.cfProvisionState === 'active' && pre?.cfSslStatus === 'active';

  const handleVerify = async ({ keepResult }: { keepResult?: boolean } = {}) => {
    // `keepResult` is set by the post-migration refresh: that refresh must NOT
    // wipe the "Migration queued…" message the operator needs to read.
    if (!keepResult) setResult(null);
    setPre(null);
    try {
      const { data, error } = await runPreflight({ variables: { stripeId: stripeId.trim() } });
      if (error) throw error;
      setPre(data?.staffBrandingMigrationPreflight ?? null);
    } catch (e: unknown) {
      setResult({ kind: 'err', text: (e as Error)?.message ?? 'Verification failed' });
    }
  };

  const handleMigrate = async () => {
    setConfirmOpen(false);
    setResult(null);
    try {
      const { data } = await migrate({ variables: { stripeId: stripeId.trim() } });
      const r = data?.staffMigrateBrandingToScio3;
      setResult({
        kind: 'ok',
        text: r?.companyName ? `${r.companyName} — ${r.message}` : (r?.message ?? 'Migrated.'),
      });
      // Refresh so the card shows the new version / provisioning state, and so a
      // second click can't re-submit an already-migrated account. Keep the result
      // message — it carries the "re-run Verify until active" instruction.
      await handleVerify({ keepResult: true });
    } catch (e: unknown) {
      setResult({ kind: 'err', text: (e as Error)?.message ?? 'Migration failed' });
    }
  };

  return (
    <div
      className="flex flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm"
      data-testid="accounts-card-migrate-branding"
    >
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-foreground">{'Migrate Branding to scio3'}</h3>
        <p className="text-xs text-muted-foreground">
          {
            'Move a legacy-branding customer from scio1 onto the scio3 branding portal. Verify first — this cannot be undone from here.'
          }
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="migrate-branding-stripeId" className="text-xs">
          {'Stripe Customer ID'}
        </Label>
        <Input
          id="migrate-branding-stripeId"
          value={stripeId}
          onChange={(e) => {
            setStripeId(e.target.value);
            setPre(null);
            setResult(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && idValid && !verifying) handleVerify();
          }}
          placeholder="cus_..."
          data-testid="accounts-migrate-branding-stripeId"
        />
        <p className="text-[11px] text-muted-foreground">{STRIPE_HELP}</p>
      </div>

      <Button
        onClick={() => handleVerify()}
        disabled={!idValid || verifying}
        className="w-fit cursor-pointer"
        variant="outline"
        data-testid="accounts-migrate-branding-verify"
      >
        {verifying ? 'Verifying...' : 'Verify'}
      </Button>

      {pre ? (
        <div className="flex flex-col gap-2 rounded-md border bg-muted/30 p-3" data-testid="accounts-migrate-branding-preflight">
          <div className="flex flex-col gap-0.5 text-xs">
            <span className="font-medium text-foreground">{val(pre.companyName)}</span>
            <span className="text-muted-foreground">
              {'Account '}
              {pre.accountId}
              {' · '}
              {DOMAIN_TYPE_LABEL[pre.domainType] ?? pre.domainType}
              {pre.planFeature ? ` · plan ${pre.planFeature}` : ''}
            </span>
            <span className="text-muted-foreground">
              {'host '}
              {val(pre.host)}
              {' · subHost '}
              {val(pre.subHost)}
            </span>
            <span className="text-muted-foreground">
              {'version '}
              {val(pre.version)}
              {' · cfProvisionState '}
              {val(pre.cfProvisionState)}
              {' · cfSslStatus '}
              {val(pre.cfSslStatus)}
            </span>
          </div>

          <ul className="flex flex-col gap-1">
            {pre.checks.map((c) => (
              <li key={c.key} className="flex flex-col gap-0.5 text-[11px]">
                <span
                  className={cn(
                    'flex items-start gap-1.5',
                    c.ok
                      ? 'text-muted-foreground'
                      : c.blocking
                        ? 'text-destructive'
                        : 'text-foreground',
                  )}
                >
                  <span aria-hidden="true">{c.ok ? '✓' : c.blocking ? '✗' : '!'}</span>
                  <span>
                    {c.label}
                    <span className="text-muted-foreground">{` — ${val(c.actual)}`}</span>
                  </span>
                </span>
                {c.detail ? (
                  <span className="pl-4 text-muted-foreground">{c.detail}</span>
                ) : null}
              </li>
            ))}
          </ul>

          {!pre.canMigrate && alreadyMigrated ? (
            // Re-running Verify is the intended way to watch provisioning progress, so
            // an already-migrated account must NOT read as a red failure — the only
            // failing check is `notMigrated`, which is the success condition here.
            <p className="text-[11px] font-medium text-foreground" data-testid="accounts-migrate-branding-migrated">
              {provisioned
                ? 'Already migrated — provisioning complete (cfProvisionState and cfSslStatus both active).'
                : 'Already migrated — waiting on the provisioning job. Re-run Verify until cfProvisionState and cfSslStatus both read "active".'}
            </p>
          ) : !pre.canMigrate ? (
            <p className="text-[11px] font-medium text-destructive">
              {'Blocked — resolve the failed checks above before migrating.'}
            </p>
          ) : null}
        </div>
      ) : null}

      {pre?.canMigrate ? (
        <Button
          onClick={() => setConfirmOpen(true)}
          disabled={migrating}
          className="w-fit cursor-pointer"
          data-testid="accounts-migrate-branding-submit"
        >
          {migrating ? 'Migrating...' : 'Migrate to scio3'}
        </Button>
      ) : null}

      {result ? (
        <p
          className={cn(
            'text-xs',
            result.kind === 'ok' ? 'text-muted-foreground' : 'text-destructive',
          )}
          data-testid="accounts-migrate-branding-result"
        >
          {result.text}
        </p>
      ) : null}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{'Migrate branding to scio3?'}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="flex flex-col gap-2 text-xs">
                <span>
                  <span className="font-medium text-foreground">{val(pre?.companyName)}</span>
                  {' ('}
                  {pre?.accountId}
                  {') — '}
                  {val(pre?.host ?? pre?.subHost)}
                </span>

                <span className="flex flex-col gap-0.5 font-mono">
                  {pre?.willGrantAddOn ? (
                    <span>
                      {'companies.feature.brandingCustomDomain: '}
                      {val(pre?.checks.find((c) => c.key === 'addOn')?.actual)}
                      {' → true'}
                    </span>
                  ) : null}
                  <span>
                    {'domains.version: '}
                    {val(pre?.version)}
                    {' → 2'}
                  </span>
                  <span>
                    {'domains.cfProvisionState: '}
                    {val(pre?.cfProvisionState)}
                    {' → pending'}
                  </span>
                </span>

                {pre?.willGrantAddOn ? (
                  <span className="text-foreground">
                    {
                      'This grandfathers the account onto the custom-domain add-on at NO CHARGE ($5/mo value).'
                    }
                  </span>
                ) : null}

                <span>
                  {
                    'No revert in this tool — rolling back requires the ops runbook (flip version to 1 and remove the override DNS record).'
                  }
                </span>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{'Cancel'}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleMigrate();
              }}
              data-testid="accounts-migrate-branding-confirm"
            >
              {'Migrate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
